import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { loadContent, loadTrees } from "./load";
import type { ContentLanguage } from "./manifest";
import type { Tree } from "./types";

type State = "loading" | "ready" | "error";

interface Catalogue {
  state: State;
  trees: Tree[];
  /** Resolves a content key from the tree data. Returns "" for null or unknown keys. */
  content: (key: string | null | undefined) => string;
}

const CatalogueContext = createContext<Catalogue>({ state: "loading", trees: [], content: () => "" });

export function useCatalogue(): Catalogue {
  return useContext(CatalogueContext);
}

export function ContentProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const language = i18n.language as ContentLanguage;

  const [state, setState] = useState<State>("loading");
  const [trees, setTrees] = useState<Tree[]>([]);
  const [strings, setStrings] = useState<Record<string, string>>({});

  useEffect(() => {
    let live = true;
    setState("loading");

    Promise.all([loadTrees(), loadContent(language)])
      .then(([document, text]) => {
        if (!live) return;
        setTrees(document.trees);
        setStrings(text);
        setState("ready");
      })
      .catch((error: unknown) => {
        // The view can only say "reload the page"; the cause belongs in the console.
        console.error(`Failed to load the catalogue for "${language}":`, error);
        if (live) setState("error");
      });

    return () => {
      live = false;
    };
  }, [language]);

  const value = useMemo<Catalogue>(
    () => ({ state, trees, content: (key) => (key ? strings[key] ?? "" : "") }),
    [state, trees, strings],
  );

  return <CatalogueContext.Provider value={value}>{children}</CatalogueContext.Provider>;
}
